import React, { useCallback, useMemo } from 'react';
import NavBar from '../../components/Navbar/NavBar';
import FooterOne from '../../components/Footer/Footer';
import { Box, Card, CardContent, CardActions, CardMedia, Container, Grid, Typography, useTheme, useMediaQuery, Link } from '@mui/material';
import { pressItems } from '../../utils/BeadsData';
import { useImageLoading } from '../../hooks/useImageLoading';
import Spinner from '../../components/common/Spinner';


const Press = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const cardStyles = {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        height: '100%',
        // minHeight: '360px',
        borderRadius: '1.5rem',
        border: '1px solid transparent',
        boxShadow: 5,
        overflow: 'hidden',
        transition: 'background-color .5s ease, color .5s ease',
        '&:hover': {
            backgroundColor: '#0d6efd',
            color: '#ffffff',
            '& .MuiTypography-h5, & a': {
                color: '#f39c12',
            },
            '& .MuiTypography-body1, & a': {
                color: '#ffffff',
            },
        },
        marginBottom: theme.spacing(4),
    };


    const cardContentStyles = {
        flex: '1 1 auto',
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '& .MuiTypography-h5': {
            textAlign: 'center',
            color: '#0d6efd',
            fontWeight: '600',
        },
        margin: isMobile ? theme.spacing(2) : 0,
    };

    const cardActionsStyles = {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flex: '0 0 auto',
        padding: theme.spacing(2),
        paddingRight: theme.spacing(2),
        // width: isMobile ? '100%' : '50%',
        width: isMobile ? '100%' : 'auto',
    };

    const imageStyles = {
        height: 200,
        width: 'auto',
        objectFit: 'cover',
        borderRadius: theme.shape.borderRadius,
        marginTop: isMobile ? theme.spacing(2) : 0,
        mx: isMobile ? 'auto' : theme.spacing(2),
        // position: 'relative', // Needed for pseudo-element positioning
        // borderRight: '2px solid #0d6efd', // Right border for non-hovered state
        // borderTop: '2px solid #0d6efd', // Top border for non-hovered state
        // '&:before': { // Pseudo-element for the left border
        //     content: '""', // Required property for pseudo-elements
        //     position: 'absolute',
        //     top: 0, // Align with the top of the image
        //     left: 0, // Align with the left of the image
        //     height: 'calc(100% - 30px)', // Height of the line, adjusted to not reach the bottom of the image
        //     width: '2px', // Thickness of the line
        //     backgroundColor: '#0d6efd', // Color of the line
        //     transition: 'background-color .5s ease', // Smooth transition for the hover effect
        // },
    };

    const renderDescription = useCallback((description) => {
        if (!Array.isArray(description)) return description;
        return description.map((segment, segIndex) => {
            if (typeof segment === 'string') {
                return <React.Fragment key={segIndex}>{segment}</React.Fragment>;
            }
            return (
                <Link
                    key={segment.text ?? segIndex}
                    href={segment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ color: '#f39c12', fontWeight: 'bold', mx: 0.5 }}
                >
                    {segment.text}
                </Link>
            );
        });
    }, []);

    const safePressItems = useMemo(() => pressItems ?? [], []);

    const projectImages = useMemo(() => {
        return safePressItems.map((item) => item.imgSrc).filter(Boolean);
    }, [safePressItems]);

    const isLoading = useImageLoading(projectImages);

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <Box>
            <NavBar />
            <Container sx={{ my: 12 }}>
                <Box sx={{ mb: 2, textAlign: 'center', margin: isMobile ? '1rem' : '2rem' }}>
                    <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2.5 }}>Press</Typography>
                    <Typography variant="h4" sx={{ mb: 2.5 }}>BEADS MAKES THE HEADLINES!</Typography>
                    <Typography variant="h5" sx={{ mb: 2.5 }}>VIEW OUR PRESS CLIPPINGS BELOW:</Typography>
                    <Typography variant="body1" sx={{ mb: 2.5 }}>Click on the titles/images to either download the PDF or view the site-link :</Typography>
                </Box>

                <Grid container spacing={4}>
                    {safePressItems.map((item) => {
                        const linkProps = item.href?.endsWith('.pdf')
                            ? { href: item.href, download: true }
                            : { href: item.href, target: '_blank', rel: 'noopener noreferrer' };

                        return (
                        <Grid item xs={12} sm={12} lg={6} key={item.title} >
                            <Card sx={cardStyles}>
                                <Box sx={{ width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
                                    {isMobile && (
                                        <CardMedia
                                            component="img"
                                            sx={imageStyles}
                                            image={item.imgSrc}
                                            alt={item.title}
                                        />
                                    )}
                                    <CardContent sx={cardContentStyles}>
                                        <Typography gutterBottom variant="h5" component="div">
                                            {item.title}
                                        </Typography>
                                        <Typography variant="body1">
                                            {renderDescription(item.description)}
                                        </Typography>
                                        {item.externalLinks?.map((link, linkIndex) => (
                                            <Typography key={link.label ?? linkIndex} variant="body1">
                                                <Link href={link.url} target="_blank" rel="noopener noreferrer" underline="hover">
                                                    {link.label}
                                                </Link>
                                            </Typography>
                                        ))}
                                    </CardContent>
                                    {!isMobile && (
                                        <CardActions sx={cardActionsStyles}>
                                            <Link
                                                {...linkProps}
                                                underline="none"
                                                aria-label={`Open press resource for ${item.title}`}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    sx={imageStyles}
                                                    image={item.imgSrc}
                                                    alt={item.title}
                                                />
                                            </Link>
                                        </CardActions>
                                    )}
                                </Box>
                            </Card>
                        </Grid>
                    )})}
                </Grid>
            </Container>
            <FooterOne />
        </Box>
    );
};

export default Press;